import { supabase } from "@/lib/supabase";
import { normalizeUserRole } from "@/data/RoleContext";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

export type SettingsType = "preferences" | "routing" | "branding" | "company" | "billing" | "integration" | "role_permissions";

export interface SettingsRow {
  id: string;
  setting_type: SettingsType;
  settings_json: Record<string, unknown>;
  organization_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PreferencesSettings {
  emailNotifications: boolean;
  issueEscalation: boolean;
  dailyDigest: boolean;
  autoAdvance: boolean;
  qaRequirements: boolean;
  dateFormat: string;
  timezone: string;
  defaultBranch: string;
}

export interface RoutingSettings {
  maxTravelDistanceMiles: number;
  preferTerritoryMatch: boolean;
  enableClusterOptimization: boolean;
  travelWarningThreshold: "moderate" | "strict";
}

export interface BrandingSettings {
  logo: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  darkMode: boolean;
}

export interface CompanySettings {
  companyName: string;
  legalName: string;
  dbaName: string;
  ein: string;
  licenseNumber: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  branches: Array<{ id: string; name: string; region: string; status: string; address: string }>;
  users: Array<{ id: string; name: string; email: string; role: string; status: string; branch: string }>;
}

export interface BillingSettings {
  billingContactEmail: string;
  paymentMethod: string;
  planTier: string;
}

export interface IntegrationSettings {
  boxHeroWebhook: string | null;
  goHighLevelEnabled: boolean;
}

export const DEFAULT_PREFERENCES: PreferencesSettings = {
  emailNotifications: true,
  issueEscalation: true,
  dailyDigest: false,
  autoAdvance: false,
  qaRequirements: true,
  dateFormat: "MM/DD/YYYY",
  timezone: "America/New_York",
  defaultBranch: "Tampa HQ",
};

export const DEFAULT_ROUTING: RoutingSettings = {
  maxTravelDistanceMiles: 30,
  preferTerritoryMatch: true,
  enableClusterOptimization: true,
  travelWarningThreshold: "moderate",
};

export const DEFAULT_BRANDING: BrandingSettings = {
  logo: null,
  primaryColor: "#2563EB",
  secondaryColor: "#64748B",
  accentColor: "#10B981",
  darkMode: false,
};

export const DEFAULT_COMPANY: CompanySettings = {
  companyName: "Koredor",
  legalName: "",
  dbaName: "",
  ein: "",
  licenseNumber: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  branches: [],
  users: [],
};

export interface ProfileRow {
  id: string;
  auth_user_id: string | null;
  role?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  branch_name: string | null;
  status: string;
  last_login: string | null;
  organization_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface BranchRow {
  id: string;
  name: string;
  region: string;
  municipality: string | null;
  state: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const DEFAULT_BILLING: BillingSettings = {
  billingContactEmail: "billing@koredor.com",
  paymentMethod: "invoice",
  planTier: "enterprise",
};

export const DEFAULT_INTEGRATION: IntegrationSettings = {
  boxHeroWebhook: null,
  goHighLevelEnabled: false,
};

export type RolePermissionsSettings = Record<string, string[]>;

export const DEFAULT_ROLE_PERMISSIONS: RolePermissionsSettings = {
  super_admin: [
    "View Jobs", "Create Jobs", "Edit Jobs", "Delete Jobs", "Approve QA", "Close Jobs", "View Job Financials",
    "View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Ordering", "Create Purchase Orders", "Approve Purchase Orders", "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Execution", "Manage Execution", "Mark Phase Complete",
    "Manage Contractors", "Approve Contractor Onboarding", "Manage Installer Offers",
    "View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources",
    "View Financials", "Manage Financials", "View Commissions", "Manage Commissions", "Approve Payments",
    "View Reports", "Export Reports",
    "Manage Users", "Manage Settings", "Manage Roles & Permissions",
  ],
  owner: [
    "View Jobs", "Create Jobs", "Edit Jobs", "Delete Jobs", "Approve QA", "Close Jobs", "View Job Financials",
    "View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Ordering", "Create Purchase Orders", "Approve Purchase Orders", "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Execution", "Manage Execution", "Mark Phase Complete",
    "Manage Contractors", "Approve Contractor Onboarding", "Manage Installer Offers",
    "View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources",
    "View Financials", "Manage Financials", "View Commissions", "Manage Commissions", "Approve Payments",
    "View Reports", "Export Reports",
    "Manage Users", "Manage Settings", "Manage Roles & Permissions",
  ],
  cfo: [
    "View Jobs", "View Job Financials",
    "View Scheduling",
    "View Issues",
    "View Ordering", "View Inventory",
    "View Execution",
    "View Customers", "View Pipeline",
    "View Financials", "View Commissions",
    "View Reports", "Export Reports",
  ],
  finance_manager: [
    "View Jobs", "View Job Financials",
    "View Issues",
    "View Ordering",
    "View Customers", "View Pipeline",
    "View Financials", "View Commissions",
    "View Reports", "Export Reports",
  ],
  bookkeeper: [
    "View Jobs", "View Job Financials",
    "View Issues",
    "View Customers",
    "View Financials", "View Commissions",
    "View Reports",
  ],
  ops_manager: [
    "View Jobs", "Create Jobs", "Edit Jobs", "Approve QA", "Close Jobs", "View Job Financials",
    "View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Ordering", "Create Purchase Orders", "Approve Purchase Orders", "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Execution", "Manage Execution", "Mark Phase Complete",
    "Manage Contractors", "Approve Contractor Onboarding", "Manage Installer Offers",
    "View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources",
    "View Financials", "View Commissions",
    "View Reports", "Export Reports",
    "Manage Users",
  ],
  admin: [
    "View Jobs", "Create Jobs", "Edit Jobs", "Delete Jobs", "Approve QA", "Close Jobs", "View Job Financials",
    "View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Ordering", "Create Purchase Orders", "Approve Purchase Orders", "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Execution", "Manage Execution", "Mark Phase Complete",
    "Manage Contractors", "Approve Contractor Onboarding", "Manage Installer Offers",
    "View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources",
    "View Financials", "Manage Financials", "View Commissions", "Manage Commissions", "Approve Payments",
    "View Reports", "Export Reports",
    "Manage Users", "Manage Settings", "Manage Roles & Permissions",
  ],
  it_staff: [
    "View Jobs", "Create Jobs", "Edit Jobs", "Delete Jobs", "Approve QA", "Close Jobs", "View Job Financials",
    "View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Execution", "Manage Execution", "Mark Phase Complete",
    "Manage Contractors", "Approve Contractor Onboarding", "Manage Installer Offers",
    "View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources",
    "View Financials", "Manage Financials", "View Commissions", "Manage Commissions", "Approve Payments",
    "View Reports", "Export Reports",
    "Manage Users", "Manage Settings", "Manage Roles & Permissions",
  ],
  warehouse_manager: [
    "View Jobs", "Edit Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Ordering", "Create Purchase Orders", "Approve Purchase Orders",
    "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Reports",
  ],
  head_of_installation: [
    "View Jobs", "Edit Jobs", "Approve QA", "Close Jobs",
    "View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs",
    "View Issues", "Manage Issues", "Resolve Issues",
    "View Inventory",
    "View Execution", "Manage Execution", "Mark Phase Complete",
    "Manage Contractors",
    "View Reports",
  ],
  procurement_manager: [
    "View Jobs", "View Job Financials",
    "View Issues",
    "View Ordering", "Create Purchase Orders", "Approve Purchase Orders",
    "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Financials", "View Commissions",
    "View Reports", "Export Reports",
  ],
  inventory_manager: [
    "View Jobs",
    "View Issues",
    "View Ordering",
    "View Inventory", "Manage Inventory", "Mark Materials Ready",
    "View Reports",
  ],
  installer: [
    "View Jobs",
    "View Scheduling",
    "View Issues", "Manage Issues",
    "Approve QA",
  ],
  subcontractor: [
    "View Jobs",
    "View Issues",
  ],
  sales: [
    "View Jobs", "Create Jobs",
    "View Issues",
    "View Customers", "View Pipeline", "Manage Pipeline",
    "View Financials", "View Commissions",
    "View Reports",
  ],
  sales_manager: [
    "View Jobs", "Create Jobs", "Edit Jobs",
    "View Issues", "Manage Issues",
    "View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources",
    "View Financials", "View Commissions",
    "View Reports", "Export Reports",
    "Manage Contractors",
  ],
  call_center: [
    "View Jobs", "Create Jobs",
    "View Scheduling",
    "View Issues",
    "View Customers", "View Pipeline", "Manage Pipeline",
  ],
};

export const SettingsService = {
  async syncProfileRole(userId: string, role: string): Promise<void> {
    const { error } = await supabase
      .from("user_roles")
      .upsert(
        {
          user_id: userId,
          role,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

    if (error) {
      console.error("Error syncing user role:", error);
      throw error;
    }
  },

  async getByType(type: SettingsType): Promise<SettingsRow | null> {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("app_settings")
      .select("*")
      .eq("setting_type", type)
      .eq("organization_id", organizationId)
      .single();

    if (error && error.code !== "PGRST116") {
      console.error(`Error fetching ${type} settings:`, error);
      throw error;
    }

    return data;
  },

  async getPreferences(): Promise<PreferencesSettings> {
    const row = await this.getByType("preferences");
    if (!row) return DEFAULT_PREFERENCES;
    return row.settings_json as unknown as PreferencesSettings;
  },

  async getRouting(): Promise<RoutingSettings> {
    const row = await this.getByType("routing");
    if (!row) return DEFAULT_ROUTING;
    return row.settings_json as unknown as RoutingSettings;
  },

  async getBranding(): Promise<BrandingSettings> {
    const row = await this.getByType("branding");
    if (!row) return DEFAULT_BRANDING;
    return row.settings_json as unknown as BrandingSettings;
  },

  async getCompany(): Promise<CompanySettings> {
    const row = await this.getByType("company");
    if (!row) return DEFAULT_COMPANY;
    return row.settings_json as unknown as CompanySettings;
  },

  async getBilling(): Promise<BillingSettings> {
    const row = await this.getByType("billing");
    if (!row) return DEFAULT_BILLING;
    return row.settings_json as unknown as BillingSettings;
  },

  async getIntegration(): Promise<IntegrationSettings> {
    const row = await this.getByType("integration");
    if (!row) return DEFAULT_INTEGRATION;
    return row.settings_json as unknown as IntegrationSettings;
  },

  async getRolePermissions(): Promise<RolePermissionsSettings> {
    const row = await this.getByType("role_permissions");
    if (!row) return DEFAULT_ROLE_PERMISSIONS;
    return row.settings_json as unknown as RolePermissionsSettings;
  },

  async saveRolePermissions(perms: RolePermissionsSettings): Promise<void> {
    const organizationId = await getCurrentOrganizationId();
    const { error } = await supabase
      .from("app_settings")
      .upsert(
        {
          setting_type: "role_permissions",
          settings_json: perms as unknown as Record<string, unknown>,
          organization_id: organizationId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "setting_type,organization_id" }
      );
    if (error) throw error;
  },

  async getProfiles(): Promise<ProfileRow[]> {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .order("full_name", { ascending: true });

    if (error) {
      console.error("Error fetching profiles:", error);
      throw error;
    }

    const profiles = (data || []) as ProfileRow[];

    if (profiles.length === 0) {
      return profiles;
    }

    const { data: roleRows, error: roleError } = await supabase
      .from("user_roles")
      .select("user_id, role")
      .in("user_id", profiles.map((profile) => profile.id));

    if (roleError) {
      console.error("Error fetching user roles:", roleError);
      throw roleError;
    }

    const rolesByUserId = new Map(
      (roleRows || []).map((row) => [row.user_id, normalizeUserRole(row.role)]),
    );

    return profiles.map((profile) => ({
      ...profile,
      role: rolesByUserId.get(profile.id) ?? normalizeUserRole(profile.role),
    }));
  },

  async getBranches(): Promise<BranchRow[]> {
    const { data, error } = await supabase
      .from("branches")
      .select("*")
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching branches:", error);
      throw error;
    }

    return data || [];
  },

  async updateProfile(id: string, updates: Partial<ProfileRow>): Promise<ProfileRow> {
    // Remove any generated columns that can't be updated directly
     
    const { full_name, role, ...safeUpdates } = updates;
    
    const { data, error } = await supabase
      .from("profiles")
      .update({
        ...safeUpdates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating profile:", error);
      throw error;
    }

    if (role) {
      await this.syncProfileRole(id, role);
    }

    return {
      ...(data as ProfileRow),
      ...(role ? { role } : {}),
    };
  },

  async updateProfileStatus(id: string, status: string): Promise<ProfileRow> {
    return this.updateProfile(id, { status });
  },

  async inviteProfile(email: string, fullName: string, role: string, branchName: string | undefined, organizationId: string): Promise<ProfileRow> {
    // Split full name into first/last for generated column
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    const newProfile: Partial<ProfileRow> = {
      id: crypto.randomUUID(),
      auth_user_id: null,
      email: email.toLowerCase().trim(),
      first_name: firstName,
      last_name: lastName,
      branch_name: branchName || null,
      status: "Invited",
      last_login: null,
      organization_id: organizationId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("profiles")
      .insert(newProfile)
      .select()
      .single();

    if (error) {
      console.error("Error inviting profile:", error);
      throw error;
    }

    await this.syncProfileRole(data.id, role);

    return {
      ...(data as ProfileRow),
      role,
    };
  },

  async upsert(
    type: SettingsType,
    settings: Record<string, unknown>,
    userId?: string
  ): Promise<SettingsRow> {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("app_settings")
      .upsert(
        {
          setting_type: type,
          settings_json: settings,
          created_by: userId ?? null,
          organization_id: organizationId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "setting_type,organization_id" }
      )
      .select("*")
      .single();

    if (error) {
      console.error(`Error upserting ${type} settings:`, error);
      throw error;
    }

    return data;
  },

  async getAll(): Promise<{
    preferences: PreferencesSettings;
    routing: RoutingSettings;
    branding: BrandingSettings;
    company: CompanySettings;
    billing: BillingSettings;
    integration: IntegrationSettings;
  }> {
    const [preferences, routing, branding, company, billing, integration] =
      await Promise.all([
        this.getPreferences(),
        this.getRouting(),
        this.getBranding(),
        this.getCompany(),
        this.getBilling(),
        this.getIntegration(),
      ]);

    return {
      preferences,
      routing,
      branding,
      company,
      billing,
      integration,
    };
  },

  async getAuditLog(limit = 100): Promise<AuditLogEntry[]> {
    const { data, error } = await supabase
      .from("job_audit_log")
      .select("id, job_id, action, changed_fields, snapshot, full_name, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data ?? []) as AuditLogEntry[];
  },
};

export interface AuditLogEntry {
  id: string;
  job_id: string;
  action: "create" | "update";
  changed_fields: Record<string, { old: unknown; new: unknown }> | null;
  snapshot: Record<string, unknown> | null;
  full_name: string | null;
  created_at: string;
}
